import { useCallback, useState } from 'react';

import { SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';
import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useNetworkUsage } from '@/features/monitoring';
import type { NetworkUsageData } from '@/features/monitoring/api';

import { CompactDualMetricCard } from '@/widgets/monitoring/CompactDualMetricCard';
import { MetricDetailModal } from '@/widgets/monitoring/MetricDetailModal';

import styles from './IOMetrics.module.css';

const NETWORK_RECEIVE_COLOR = SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.INDIGO;
const NETWORK_TRANSMIT_COLOR = SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.MAGENTA;

type IOMetricsContentProps = {
  data?: NetworkUsageData;
  isLoading?: boolean;
};

type IOMetricsViewProps = {
  data?: NetworkUsageData;
  isLoading?: boolean;
};

const IOMetricsView = ({ data: networkData, isLoading: networkLoading = false }: IOMetricsViewProps) => {
  const [networkModalOpen, setNetworkModalOpen] = useState(false);

  const handleDetailClick = useCallback(() => {
    setNetworkModalOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.container}>
      <CompactDualMetricCard
        title="Network I/O"
        primaryLabel="수신"
        primaryValue={networkData?.receive.current ?? 0}
        primaryHistory={networkData?.receive.history ?? []}
        primaryPodHistory={networkData?.receive.podHistory ?? []}
        primaryColor={NETWORK_RECEIVE_COLOR}
        secondaryLabel="송신"
        secondaryValue={networkData?.transmit.current ?? 0}
        secondaryHistory={networkData?.transmit.history ?? []}
        secondaryPodHistory={networkData?.transmit.podHistory ?? []}
        secondaryColor={NETWORK_TRANSMIT_COLOR}
        unit={networkData?.unit ?? 'KB/s'}
        isLoading={networkLoading}
        onClick={handleDetailClick}
      />

      {networkData && (
        <MetricDetailModal
          open={networkModalOpen}
          onClose={handleDetailClick}
          title="Network I/O"
          primaryLabel="수신"
          primaryValue={networkData.receive.current}
          primaryHistory={networkData.receive.history}
          primaryColor={NETWORK_RECEIVE_COLOR}
          secondaryLabel="송신"
          secondaryValue={networkData.transmit.current}
          secondaryHistory={networkData.transmit.history}
          secondaryColor={NETWORK_TRANSMIT_COLOR}
          unit={networkData.unit}
        />
      )}
    </div>
  );
};

const ConnectedContent = () => {
  const { data, isLoading } = useNetworkUsage();

  return <IOMetricsView data={data} isLoading={isLoading} />;
};

const Content = ({ data, isLoading }: IOMetricsContentProps) => {
  if (data) {
    return <IOMetricsView data={data} isLoading={isLoading} />;
  }

  return <ConnectedContent />;
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.container}>
      <ErrorFallback title={title} description={description} className={styles.error} {...props} />
    </div>
  );
};

export const IOMetrics = { Content, Error };
